<template>
  <div class="booking-popup" v-if="booking">
    <div class="main-content" :class="darkMode ? 'dark' : ''">
      <div class="tiers">
        <div class="label f8">{{ $t('Length') }}</div>
        <div class="tiersgrid">
          <UIButton
            :ghost="true"
            class="tierbutton"
            v-for="tier in tiers"
            :key="tier.id"
            @click="tierAction(tier)"
            :class="{ active: !customTier && tier.id === paidExtension.tier }"
          >{{ tier.length }} {{ $t('Min') }} - {{ toLocationLocale(tier.price) }}</UIButton>
          <UIButton
            :ghost="true"
            class="customtier"
            :class="{ active: customTier }"
            @click="customTier = true"
          >{{ $t('Custom') }}</UIButton>
        </div>
      </div>
      <div class="customtierinputs" v-if="customTier">
        <UIInput
          type="number"
          min="0"
          max="99"
          @input="setCustomLength"
          :value="paidExtension.length"
          :title="$t('Custom Length')"
        />
        <UIInput type="number" min="0" max="99" v-model="customPrice" :title="$t('Custom Price')"/>
      </div>
      <div class="priceandfullbooking">
        <div class="price">
          <location-price :label="label" :amount="amount" :locationId="locationId"/>
          <UICheckbox
            v-if="!paid"
            @change="toggleMarkAsPaid"
            :checked="paid"
            :title="$t('Mark as paid')"
          />
        </div>
        <label class="fullbooking f8" v-if="booking.bookingStationTimes.length > 1">
          <input
            type="checkbox"
            @change="setExtendFullBooking"
            :checked="paidExtension.fullBooking"
          >
          {{ $t('Extend Full Reservation') }}
        </label>
      </div>
    </div>

    <div v-if="conflict" class="conflict f5">
      <div>{{ $t('This will cause a scheduling conflict.') }}</div>
    </div>
    <div class="bottombar" :class="darkMode ? 'dark' : ''">
      <UIButton
        :ghost="true"
        class="backtobookingdetail"
        @click="goToBookingDetail"
      >{{ $t('Back to Reservation') }}</UIButton>
      <UIButton class="addextension" @click="addPaidExtension">{{ $t('Add Extension') }}</UIButton>
    </div>
  </div>
</template>

<script>
import Component, { mixins } from 'vue-class-component';
import LocationPrice from '@/location/price-components/LocationPrice';
import moment from 'moment-timezone';
import conflictDetectorWorker from '@/location/conflict-detector.worker';
import UICheckbox from '@/core/shared/components/ui/UICheckbox';
import UIInput from '@/core/shared/components/ui/UIInput';
import UIButton from '@/core/shared/components/ui/UIButton';
import IsMobileMixin from '@/core/shared/misc/IsMobileMixin';
import deepmerge from 'deepmerge';

const conflictDetector = conflictDetectorWorker();

@Component({
  components: {
    LocationPrice,
    UIButton,
    UICheckbox,
    UIInput,
  },
  props: {
    locationId: {
      type: String,
      required: true,
    },
    stationId: {
      type: String,
      required: true,
    },
    bookingId: {
      type: String,
      required: true,
    },
    bookingStationTimeIndex: {
      type: [Number, String],
      required: true,
    },
    darkMode: {
      type: Boolean,
      required: false,
    },
  },
})
export default class PaidExtensionRoute extends mixins(IsMobileMixin) {
  conflict = false;
  customTier = false;
  paidExtension = {
    length: null,
    payment: null,
    amountPaid: 0,
    amountDue: 0,
    tier: null,
    fullBooking: false,
  };

  get booking () {
    const booking = this.$store.getters['entities/Booking'](this.bookingId) || {};
    return deepmerge({}, booking);
  }

  get tiers () {
    return this.$store.getters.locationExperiences(this.locationId).find(experience => {
      return experience.id === this.booking.bookingStationTimes[parseInt(this.bookingStationTimeIndex)].experience.id;
    }).tiers;
  }

  get payments () {
    return (this.booking.host && this.booking.host.cards) ? this.booking.host.cards : [];
  }

  get paid () {
    return this.paidExtension.amountDue === 0;
  }

  get amount () {
    return (this.paid) ? this.paidExtension.amountPaid : this.paidExtension.amountDue;
  }

  get label () {
    return (this.paid) ? this.$t('Amount Paid') : this.$t('Amount Unpaid');
  }

  // Need to reverse what I did in the set with the taxes if exclusive
  get customPrice () {
    const location = this.$store.getters['entities/Location'](this.locationId);
    if (location.taxExclusive) {
      return Math.round(this.amount / (location.taxAmount / 100 + 1)) / 100;
    } else {
      return this.amount / 100;
    }
  }

  set customPrice (price) {
    price = this.$store.getters.tierPriceToLocationPrice({ locationId: this.locationId, tierPrice: price * 100 });
    if (this.paid) {
      this.paidExtension.amountPaid = price;
    } else {
      this.paidExtension.amountDue = price;
    }
  }

  getPackagedPaidExtension () {
    return {
      stationId: this.stationId,
      bookingId: this.bookingId,
      locationId: this.locationId,
      ...this.paidExtension,
    };
  }

  created () {
    this.init();
  }

  init () {
    if (this.booking) {
      this.paidExtension.tier = this.tiers[0].id;
      this.paidExtension.length = this.tiers[0].length;
      if (this.payments.length) {
        this.paidExtension.payment = this.payments[0].id;
        this.paidExtension.amountPaid = this.tiers[0].price;
      } else {
        this.paidExtension.amountDue = this.tiers[0].price;
      }

      this.generateConflicts();
    } else {
      setTimeout(() => {
        this.init();
      }, 1000);
    }
  }

  tierAction (tier) {
    this.customTier = false;
    this.paidExtension.tier = tier.id;
    this.paidExtension.length = tier.length;

    let amount = 0;
    const price = this.$store.getters.tierPriceToLocationPrice({ locationId: this.locationId, tierPrice: tier.price });
    if (this.paidExtension.fullBooking) {
      amount = price * this.booking.bookingStationTimes.length;
    } else {
      amount = price;
    }

    if (this.paid) {
      this.paidExtension.amountPaid = amount;
    } else {
      this.paidExtension.amountDue = amount;
    }

    this.generateConflicts();
  }

  paymentAction (payment) {
    if (payment) {
      this.paidExtension.payment = payment.id;
      if (!this.paid) {
        this.paidExtension.amountPaid = this.paidExtension.amountDue;
        this.paidExtension.amountDue = 0;
      }
    } else {
      this.paidExtension.payment = null;
    }
  }

  setCustomLength (e) {
    this.paidExtension.length = parseInt(e.target.value);
    this.generateConflicts();
  }

  setExtendFullBooking (e) {
    this.paidExtension.fullBooking = e.target.checked;

    let newAmount = this.amount;
    if (this.paidExtension.fullBooking) {
      newAmount = newAmount * this.booking.bookingStationTimes.length;
    } else {
      newAmount = newAmount / this.booking.bookingStationTimes.length;
    }

    if (this.paid) {
      this.paidExtension.amountPaid = newAmount;
    } else {
      this.paidExtension.amountDue = newAmount;
    }

    this.generateConflicts();
  }

  toggleMarkAsPaid () {
    if (this.paid) {
      this.paidExtension.amountDue = this.paidExtension.amountPaid;
      this.amountPaid = 0;
    } else {
      this.paidExtension.amountPaid = this.paidExtension.amountDue;
      this.paidExtension.amountDue = 0;
    }
  }

  toLocationLocale (number) {
    return this.$store.getters.formatAsLocationCurrency({ locationId: this.locationId, number });
  }

  async generateConflicts () {
    const bookings = this.$store.getters.locationBookings(this.locationId).map(booking => deepmerge({}, booking));
    const mockBooking = { ...this.booking };
    mockBooking.bookingStationTimes.forEach(bookingStationTime => {
      if (this.paidExtension.fullBooking || bookingStationTime.station.id === this.stationId) {
        bookingStationTime.endTime = moment(bookingStationTime.endTime).add(this.paidExtension.length, 'minutes').format();
      }
    });
    this.conflict = await conflictDetector.willBookingCauseConflict(mockBooking, bookings);
  }

  goToBookingDetail () {
    const params = {
      locationId: this.locationId,
      stationId: this.stationId,
      bookingId: this.bookingId,
    };
    if (this.isMobile) {
      this.$router.push({
        name: 'mobile-booking-detail',
        params,
      });
    } else {
      this.$store.commit('setOverviewScrollPosition');
      this.$router.push({
        name: 'booking-detail',
        params,
      });
    }
  }

  addPaidExtension (paidExtension) {
    this.$store.dispatch('addPaidExtension', this.getPackagedPaidExtension());
    this.goToBookingDetail();
  }
}
</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.booking-popup {
  & .main-content {
    position: relative;
    display: grid;
    grid-gap: var(--spacingMd);
    padding: var(--spacingMd);

    @media (--desktop) {
      border-bottom: 0.1rem solid var(--colorCasper);
    }

    & .label,
    & label {
      margin-bottom: var(--spacingXS);
    }

    & .tiersgrid {
      display: grid;
      grid-gap: var(--spacingSm);

      @media (--tablet) {
        grid-template-columns: repeat(3, 1fr);
      }
    }

    & .customtierinputs {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: var(--spacingSm);
      margin-top: var(--spacingSm);

      @media (--tablet) {
        justify-content: flex-start;
      }

      & .group {
        margin-right: var(--spacingSm);
        display: flex;
        align-items: center;

        &:last-child {
          margin-right: 0;
        }

        & label {
          margin-right: var(--spacingXS);
        }

        & input {
          padding: var(--spacingXS);
        }
      }
    }

    & .priceandfullbooking {
      display: flex;
      align-items: center;

      & .price {
        margin-right: var(--spacingMd);
      }
    }
  }

  & .conflict {
    margin: 0;
    padding: var(--spacingXS);
    background-color: var(--colorErrorFlat);
    color: var(--colorWhite);
    text-align: center;
  }

  & .bottombar {
    padding: var(--spacingSm) var(--spacingMd);
    justify-content: flex-end;

    @media (--tablet) {
      display: flex;
    }

    & .backtobookingdetail {
      width: 100%;
      margin-bottom: var(--spacingSm);

      @media (--tablet) {
        margin-right: var(--spacingMd);
        margin-bottom: 0;
      }
    }

    & .addextension {
      width: 100%;
    }
  }
}

.main-content.dark {
  background-color: var(--darkModeColorDarkBackground);
  color: var(--darkModeColorText) !important;
  & .tiers .label {
  color: var(--darkModeColorText) !important;
  }
  & .tiers .tiersgrid .tierbutton {
    background-color: var(--darkModeColorDarkBackground);
    color: var(--darkModeColorText) !important;
  }
  & .tiers .tiersgrid .active {
    background-color: var(--darkModeColorLightBackground);
  }
  & .customtier {
    background-color: var(--darkModeColorDarkBackground);
    color: var(--darkModeColorText) !important;
  }
  & .customtierinputs div /deep/ .label {
    color: var(--darkModeColorText) !important;
  }
  & .customtierinputs div /deep/ .ui-input {
    background-color: var(--darkModeColorDarkBackground);
    color: var(--darkModeColorText) !important;
  }
  & .priceandfullbooking .price .location-price /deep/ .taxrow .taxes {
    color: var(--darkModeColorText) !important;
  }
  & .priceandfullbooking .price div /deep/ .ui-checkbox .box-and-title .title {
    color: var(--darkModeColorText) !important;
  }
}

.bottombar.dark {
  background-color: var(--darkModeColorDarkBackground);
  color: var(--darkModeColorText) !important;
  & .backtobookingdetail {
  background-color: var(--darkModeColorDarkBackground);
  color: var(--darkModeColorText) !important;
  }
}
</style>
